'use client'

import { type RefObject, useEffect, useRef, useState } from 'react'

import type { TwinController, TwinHandlers, TwinState } from './twinTypes'
import { FORWARD_LABELS, LEVEL_NAMES, type HotspotId } from './twinContent'

// What the rail renders before the scene has reported anything. Matches the scene's own starting
// point (the aisle), so the markup never flashes a different level.
const INITIAL: TwinState = {
  view: 'hall',
  step: 0,
  stepTotal: 4,
  levelName: LEVEL_NAMES.hall,
  forwardLabel: FORWARD_LABELS.desktop.hall,
  canGoBack: false,
  canGoForward: true,
  ready: false,
}

export function useTwinScene(
  rootRef: RefObject<HTMLDivElement | null>,
  onInteract: TwinHandlers['onInteract'],
) {
  const [state, setState] = useState<TwinState>(INITIAL)
  const [panelId, setPanelId] = useState<HotspotId | null>(null)
  // WebGLRenderer throws where WebGL is off or the context limit is spent, so `ready` would
  // otherwise never arrive and the loading board would sit there for good.
  const [failed, setFailed] = useState(false)
  const controller = useRef<TwinController | null>(null)
  const interact = useRef(onInteract)
  useEffect(() => {
    interact.current = onInteract
  }, [onInteract])

  useEffect(() => {
    if (!rootRef.current) return

    let dispose: (() => void) | undefined
    let cancelled = false

    // three is imported here rather than at module scope so it stays out of the route's initial JS
    import('./scene')
      .then(({ createTwinScene }) => {
        if (cancelled || !rootRef.current) return
        const scene = createTwinScene(rootRef.current, {
          onState: setState,
          onOpenPanel: setPanelId,
          onClosePanel: () => setPanelId(null),
          onInteract: (kind, view) => interact.current(kind, view),
        })
        controller.current = scene
        dispose = scene.destroy
      })
      .catch((err) => {
        console.error('[vera-rubin-twin] scene failed to load', err)
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
      controller.current = null
      dispose?.()
    }
  }, [rootRef])

  return { state, panelId, controller, failed }
}
